<template>
	<div class="form-container">
		<CRow>
			<CCol md="12">
				<h2 class="typo-h4 position-relative">
					General info
				</h2>
			</CCol>
			<CCol md="6" class="mt-2">
				<CInput
					v-model.trim="$v.formData.nameTh.$model"
					:is-valid="!$v.formData.nameTh.$error && null"
					label="Name (TH)*"
					placeholder="Name (TH)"
					invalid-feedback="Field Length: 1 - 255 characters"
					@blur="handleAutoFillSlug"
				/>
			</CCol>
			<CCol md="6" class="mt-2">
				<CInput
					v-model.trim="$v.formData.nameEn.$model"
					:is-valid="!$v.formData.nameEn.$error && null"
					label="Name (EN)*"
					placeholder="Name (EN)"
					invalid-feedback="Field Length: 1 - 255 characters"
					@blur="handleAutoFillSlug"
				/>
			</CCol>
			<CCol md="12" class="mt-2">
				<CInput
					v-model.trim="$v.formData.customUrl.$model"
					:is-valid="!$v.formData.customUrl.$error && null"
					:invalid-feedback="'Invalid url format'"
					:description="'อนุญาตเฉพาะรูปแบบ URL เท่านั้น ตัวอย่าง: https://www.studio7thailand.com'"
					label="Custom URL"
				/>
			</CCol>
			<CCol md="12" class="mt-2">
				<CInput
					v-model.trim="$v.formData.slug.$model"
					:is-valid="!$v.formData.slug.$error && null"
					:description="slugUrl"
					label="Slug*"
					placeholder="slug-url"
					invalid-feedback="Field Length: 1 - 255 characters"
				/>
			</CCol>
			<CCol md="12" class="mt-3 d-flex align-items-cente">
				<label class="toggle-label mb-0">Status</label>
				<CSwitch
					:checked="isActive"
					variant="3d"
					size="sm"
					class="switch-custom"
					color="success"
					@update:checked="handleChangeStatus"
				/>
				<span class="ml-2">{{ statusText }}</span>
			</CCol>
			<CCol md="12" class="mt-3 d-flex align-items-cente">
				<label class="toggle-label mb-0">Store-front visibility</label>
				<CSwitch
					:checked.sync="formData.isVisible"
					variant="3d"
					size="sm"
					class="switch-custom"
					color="success"
				/>
				<span class="ml-2">{{ getVisibilityText(formData.isVisible) }}</span>
			</CCol>

			<CCol md="12" class="mt-4">
				<hr>
			</CCol>

			<CCol md="12" class="mt-4">
				<h2 class="typo-h4">
					Active dates
				</h2>
				<div class="d-flex alert alert-warning mb-3" role="alert">
					<CIcon class="icon-warning" name="cil-warning" />
					<p class="typo-body-2 mb-0">
						ระบบจะทำการตรวจสอบ Start/End Date ทุกๆ 1 นาที หากวันเวลาปัจจุบันอยู่นอกเหนือจากช่วงเวลาดังกล่าว สถานะจะถูกปรับเป็น Inactive โดยอัตโนมัติ
					</p>
				</div>
			</CCol>
			<CCol md="6" class="mt-2">
				<BaseInputDate
					v-model="$v.formData.startDate.$model"
					:is-valid="!$v.formData.startDate.$error"
					label="Start date*"
					invalid-feedback="Start date is required"
					placeholder="Select date"
					@input="handleActiveStartDateChange"
				>
					<template #append-content>
						<CIcon class="cil-calendar" name="cil-calendar" />
					</template>
				</BaseInputDate>
			</CCol>
			<CCol md="6" class="mt-2">
				<BaseInputTime
					v-model="$v.formData.startTime.$model"
					:is-valid="!$v.formData.startTime.$error"
					:invalid-feedback="errorStartTime"
					label="Start time*"
				/>
			</CCol>

			<CCol md="6" class="mt-3">
				<BaseInputDate
					v-model="$v.formData.endDate.$model"
					:disabled="$v.formData.startDate.$invalid"
					:is-valid="!$v.formData.endDate.$error"
					:min-date="formData.startDate"
					label="End date*"
					invalid-feedback="End date is required"
					placeholder="Select date"
				>
					<template #append-content>
						<CIcon class="cil-calendar" name="cil-calendar" />
					</template>
				</BaseInputDate>
			</CCol>
			<CCol md="6" class="mt-3">
				<BaseInputTime
					v-model="$v.formData.endTime.$model"
					:is-valid="!$v.formData.endTime.$error"
					:invalid-feedback="errorEndTime"
					label="End time*"
				/>
			</CCol>

			<CCol md="12" class="mt-4">
				<hr>
			</CCol>

			<!-- SEO -->
			<CCol md="12" class="mt-4">
				<h2 class="typo-h4">
					SEO
				</h2>
			</CCol>

			<CCol md="12" class="mt-2">
				<label class="d-block">Meta title</label>
				<CRow>
					<CCol md="6">
						<CInput
							v-model="$v.formData.metaTitleTh.$model"
							label="TH"
							placeholder="Meta title (TH)"
						/>
					</CCol>
					<CCol md="6">
						<CInput
							v-model="$v.formData.metaTitleEn.$model"
							label="EN"
							placeholder="Meta title (EN)"
						/>
					</CCol>
				</CRow>
			</CCol>
			<CCol md="12" class="mt-2">
				<label class="d-block">Meta keyword</label>
				<CRow>
					<CCol md="6">
						<CTextarea
							v-model="$v.formData.metaKeyTh.$model"
							label="TH"
							placeholder="Meta keyword (TH)"
							rows="3"
						/>
					</CCol>
					<CCol md="6">
						<CTextarea
							v-model="$v.formData.metaKeyEn.$model"
							label="EN"
							placeholder="Meta keyword (EN)"
							rows="3"
						/>
					</CCol>
				</CRow>
			</CCol>
			<CCol md="12" class="mt-2">
				<label class="d-block">Meta description</label>
				<CRow>
					<CCol md="6">
						<CTextarea
							v-model="$v.formData.metaDescTh.$model"
							label="TH"
							placeholder="Meta description (TH)"
							rows="4"
						/>
					</CCol>
					<CCol md="6">
						<CTextarea
							v-model="$v.formData.metaDescEn.$model"
							label="EN"
							placeholder="Meta description (EN)"
							rows="4"
						/>
					</CCol>
				</CRow>
			</CCol>

			<CCol md="12" class="mt-2">
				<CRow>
					<CCol md="12">
						<BaseMediaSelectorBox
							v-model="formData.metaImage"
							title="Meta image"
							no-image-text="No meta image"
						/>
					</CCol>
				</CRow>
			</CCol>
		</CRow>
	</div>
</template>

<script>
import { required, maxLength, url } from 'vuelidate/lib/validators';
import { validationMixin } from 'vuelidate';
import dayjs from 'dayjs';
import BaseMediaSelectorBox from '@/components/BaseMediaSelectorBox.vue';
import {
	STATUS as mappingStatus,
	VISIBILITY as mappingVisibility,
} from '../mapping/pages';

const slugify = require('slugify');

const STATUS_OPTIONS = [
	{ value: 1, label: 'Active' },
	{ value: 0, label: 'Inactive' },
];

export default {
	name: 'StaticLandingPageStepGeneralInfo',

	components: {
		BaseMediaSelectorBox,
	},

	mixins: [validationMixin],

	props: {
		defaultData: {
			type: Object,
			default: null,
		},
		isEditMode: {
			type: Boolean,
			default: false,
		},
	},

	validations() {
		const base = {
			nameTh: { required, maxLength: maxLength(255) },
			nameEn: { required, maxLength: maxLength(255) },
			customUrl: { url },
			slug: { required, maxLength: maxLength(255) },
			startDate: { required },
			endDate: { required },
			startTime: { required, validTime: (time) => time.length === 5 },
			endTime: { required, validTime: (time) => time.length === 5 },
			metaTitleTh: {},
			metaTitleEn: {},
			metaKeyTh: {},
			metaKeyEn: {},
			metaDescTh: {},
			metaDescEn: {},
		};

		if (
			this.formData.startDate
			&& this.formData.endDate
			&& dayjs(this.formData.startDate).isSame(dayjs(this.formData.endDate))
		) {
			return {
				formData: {
					...base,
					endTime: {
						required,
						validTime: (time) => time.length === 5,
						after(time, vm) {
							const [timeFromHour = 0, timeFromMinute = 0] = vm.startTime.split(':');
							const [timeToHour = 0, timeToMinute = 0] = vm.endTime.split(':');
							const timeFrom = dayjs().hour(timeFromHour).minute(timeFromMinute);
							const timeTo = dayjs().hour(timeToHour).minute(timeToMinute);
							return timeFrom.isBefore(timeTo);
						},
					},
				},
			};
		}

		return { formData: base };
	},

	data() {
		const {
			nameEn = null,
			nameTh = null,
			customUrl = null,
			slug = null,
			isActive = 1,
			isVisible = true,
			startDate = null,
			startTime = '00:00',
			endDate = null,
			endTime = '23:59',
			metaTitleTh = null,
			metaTitleEn = null,
			metaKeyTh = null,
			metaKeyEn = null,
			metaDescTh = null,
			metaDescEn = null,
			metaImage = null,
		} = this.defaultData || {};

		return {
			statusOptions: STATUS_OPTIONS,
			formData: {
				nameEn,
				nameTh,
				customUrl,
				slug,
				isActive,
				isVisible,
				isHeaderVisible: false,
				isSidebarVisible: false,
				startDate,
				startTime,
				endDate,
				endTime,
				metaTitleTh,
				metaTitleEn,
				metaKeyTh,
				metaKeyEn,
				metaDescTh,
				metaDescEn,
				metaImage,
			},
		};
	},

	computed: {
		errorStartTime() {
			if (!this.$v.formData.startTime.required) return 'Required field';
			if (!this.$v.formData.startTime.validTime) return 'Time not valid';
			return null;
		},
		errorEndTime() {
			if (!this.$v.formData.endTime.required) return 'Required field';
			if (!this.$v.formData.endTime.validTime) return 'Time not valid';
			if (this.$v.formData.endTime.after === false) return `Should be after ${this.formData.startTime}`;
			return null;
		},
		isActive() {
			return !!this.formData.isActive;
		},
		statusText() {
			const text = mappingStatus[this.isActive] || mappingStatus[!!this.isActive]; // Map via true/false or 1/0
			return this.$t(text);
		},
		slugUrl() {
			return `ลิงก์แสดงผล: ${process.env.VUE_APP_ECOM_ENDPOINT}/th/pages/${this.formData.slug || ''}`;
		},
	},

	watch: {
		formData: {
			deep: true,
			handler(data) {
				this.$emit(
					'update:is-valid',
					{
						valid: !this.$v.$invalid,
						data,
					},
				);
			},
		},
	},

	methods: {
		handleChangeStatus(value) {
			this.formData.isActive = value ? 1 : 0;
		},
		handleActiveStartDateChange(value) {
			if (!this.formData.endDate || dayjs(value).isAfter(dayjs(this.formData.endDate))) {
				this.formData.endDate = value;
			}
		},
		handleAutoFillSlug() {
			const nameForSlug = this.formData.nameEn || this.formData.nameTh;
			if (!this.formData.slug && nameForSlug) {
				this.formData.slug = slugify(nameForSlug, {
					lower: true,
					strict: true,
				});
				this.$v.formData.slug.$touch();
			}
		},
		getVisibilityText(value) {
			const text = mappingVisibility[value] || '';
			return this.$t(text);
		},
	},
};
</script>

<style lang="scss" scoped>
	.form-container {
		margin-top: rem(20);
	}

	.toggle-label {
		width: rem(150);
	}

	.alert {
		padding: rem(16);

		&-warning {
			background-color: rgba($color-orange, 0.1);
			border-color: rgba($color-orange, 0.1);
		}

		.icon-warning {
			color: $color-orange;
			height: rem(24);
			width: rem(24);

			margin-right: rem(16);
		}
	}
</style>
